import { Link } from "react-router-dom";
import {
  Wallet,
  Rocket,
  ShieldCheck,
  Timer,
  ArrowRight,
  Building2,
  Landmark,
  HeartPulse,
  Film,
  ShoppingBag,
  Factory,
  BadgeCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ServiceCard } from "@/components/ServiceCard";
import { TestimonialCard } from "@/components/TestimonialCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { services } from "@/data/services";

const whyUs = [
  {
    icon: Wallet,
    title: "Keep Costs Low",
    text: "Competitive, transparent pricing models that keep your projects on budget without cutting corners.",
  },
  {
    icon: Rocket,
    title: "Deploy Resources Quicker",
    text: "Strong bench strength enables fast, efficient team scaling and engagement.",
  },
  {
    icon: ShieldCheck,
    title: "Highest Quality Standards",
    text: "Experienced teams and mature processes ensure every deliverable meets rigorous quality benchmarks.",
  },
  {
    icon: Timer,
    title: "Faster Time to Market",
    text: "Agile practices and seasoned professionals drive faster, more reliable delivery.",
  },
];

const testimonials = [
  {
    quote:
      "Stantive has been a dependable long-term partner. Their team consistently delivers on commitments and brings a level of technical depth that is hard to find.",
    name: "Priya Menon",
    title: "VP Engineering",
    company: "FinEdge Bank",
    initials: "PM",
  },
  {
    quote:
      "Proactive, transparent, and technically sharp. They feel like an extension of our own engineering org rather than an outside vendor.",
    name: "David Sokolov",
    title: "CTO",
    company: "NorthWave Media",
    initials: "DS",
  },
  {
    quote:
      "From strategy to delivery, Stantive brought clarity to a complex Salesforce rollout. Highly recommended for any serious CRM transformation.",
    name: "Aisha Rahman",
    title: "Head of CRM",
    company: "Zenith Retail",
    initials: "AR",
  },
  {
    quote:
      "Their QA automation practice cut our regression cycle by more than half. Real impact, measured in weeks not months.",
    name: "Mark Whelan",
    title: "Director of Quality",
    company: "Helix Health",
    initials: "MW",
  },
];

const industries = [
  { icon: Building2, name: "Enterprise & Fortune 500" },
  { icon: Landmark, name: "Banking & Finance" },
  { icon: HeartPulse, name: "Healthcare" },
  { icon: Film, name: "Media & Entertainment" },
  { icon: ShoppingBag, name: "Retail & E-commerce" },
  { icon: Factory, name: "Manufacturing & Logistics" },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Stantive Soft Solutions — Software, Quality, Salesforce, Cloud & AI"
        description="Stantive Soft Solutions delivers Quality Engineering, AI Engineering, Salesforce and ERP Implementation, Custom Development, and Cloud Engineering services."
        path="/"
      />
      <PageHero
        eyebrow="Trusted software partner"
        image="/images/hero-home.jpg"
        imagePosition="center"
        title={
          <>
            Empowering businesses through <span className="text-gradient">smart software</span>{" "}
            solutions.
          </>
        }
        description="Stantive Soft Solutions delivers Quality Engineering, AI Engineering, Salesforce and ERP Implementation, Custom Development, and Cloud Engineering services to companies across the globe, built on simplicity, clarity, and reliability."
        actions={
          <>
            <Button
              asChild
              size="lg"
              className="bg-gradient-hero text-primary-foreground shadow-elegant hover:opacity-90"
            >
              <Link to="/services">
                Explore Services <ArrowRight size={16} className="ml-1" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-ink-foreground/25 bg-ink/25 text-ink-foreground backdrop-blur hover:bg-ink-foreground/10"
            >
              <Link to="/contact">Talk to Us</Link>
            </Button>
          </>
        }
        stats={[
          { value: "12+", label: "Years" },
          { value: "180+", label: "Projects" },
          { value: "65+", label: "Clients" },
          { value: "99.9%", label: "Uptime" },
        ]}
      />

      {/* Salesforce Partner */}
      <section className="border-y border-border bg-secondary/40 py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-4 text-center sm:flex-row sm:justify-center sm:gap-4 sm:px-6 lg:px-8">
          <BadgeCheck className="text-primary" size={22} />
          <p className="text-sm font-medium text-muted-foreground sm:text-base">
            We are proud to be partnered with{" "}
            <span className="font-semibold text-foreground">Salesforce</span>.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              What we do
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              Our expertise, engineered for outcomes
            </h2>
            <p className="mt-4 text-muted-foreground">
              Five deep practices, one accountable partner — from first line of code to production
              at scale.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 80}>
                <ServiceCard
                  icon={s.icon}
                  title={s.title}
                  description={s.shortDescription}
                  hash={s.slug}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="bg-secondary/40 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Why Stantive
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              Built to be a long-term partner
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyUs.map((w, i) => (
              <Reveal key={w.title} delay={i * 80}>
                <div className="h-full rounded-2xl border border-border bg-card p-6 shadow-card">
                  <div className="mb-4 grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
                    <w.icon size={20} />
                  </div>
                  <h3 className="mb-2 font-semibold">{w.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* About snapshot */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-border bg-ink shadow-elegant">
              <img
                src="/images/hero-about.jpg"
                alt="Stantive team collaborating in a modern technology office"
                className="aspect-[4/3] h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-linear-to-t from-ink via-ink/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-glow">
                  Delivery principles
                </span>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {["Simplicity", "Clarity", "Reliability", "Craft"].map((v) => (
                    <div key={v} className="border border-white/15 bg-ink/55 p-4 backdrop-blur">
                      <div className="font-display text-2xl font-semibold text-primary-glow">
                        {v[0]}
                      </div>
                      <div className="mt-1 text-sm font-medium text-ink-foreground">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              About us
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              A team of technologists who care about your outcomes.
            </h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Stantive is a team of experienced engineers, architects, and consultants with a
              customer-first mindset. We believe great software is a product of simplicity, clarity,
              and reliability — and we bring that discipline to every engagement, no matter how
              complex.
            </p>
            <div className="mt-6">
              <Button asChild variant="outline">
                <Link to="/about">
                  Learn more about us <ArrowRight size={16} className="ml-1" />
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-secondary/40 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Testimonials
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              Trusted by teams that ship
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 80}>
                <TestimonialCard {...t} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Industries we serve
            </span>
            <h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">
              Domain expertise across every sector
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {industries.map((ind, i) => (
              <Reveal key={ind.name} delay={i * 60}>
                <div className="flex h-full flex-col items-center gap-2 rounded-xl border border-border bg-card p-5 text-center transition-colors hover:border-primary/40">
                  <ind.icon className="text-primary" size={22} />
                  <span className="text-xs font-medium">{ind.name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl bg-gradient-ink p-10 shadow-elegant sm:p-16">
          <div className="relative grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <h2 className="font-display text-3xl font-bold text-ink-foreground sm:text-4xl">
                Ready to build something great?
              </h2>
              <p className="mt-3 max-w-xl text-ink-muted">
                Tell us about your project. We'll respond within one business day with a concrete
                next step.
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="w-fit bg-gradient-hero text-primary-foreground hover:opacity-90"
            >
              <Link to="/contact">
                Get in Touch <ArrowRight size={16} className="ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
